/* eslint-disable */
import React, { useEffect, useMemo, useRef, useState } from "react";
import type mapboxgl from "mapbox-gl";
import * as turf from "@turf/turf";
import MapComponent from "../../../maps/components/MapComponent";
import MapErrorOverlay from "../../../maps/components/MapErrorOverlay";
import {
  clearStripLinesCache,
  clearStripLinesFromMap,
  initializeMap,
  useMapComponent,
  registerAlignmentChangeCallback,
  snapPlotsToAbLine,
} from "../../../maps/components/MapComponent/utils";

import { useNewTrialState } from "../hooks/NewTrialStateProvider";

import {
  type IMapComponentRef,
  type TrialMapComponentProps,
} from "../../../maps/interfaces/map.interface";
import useFormHandling from "../hooks/useFormHandling";
import { FieldDataInterface } from "../../../maps/components/MapComponent/type";
import { SubplotDetailsCard } from "./PlotSummary";
import MapLayersDrawer from "./MapLayersDrawer";
import AbLineAlignmentBanner from "./AbLineAlignmentBanner";
import { toast } from "react-toastify";
import {
  addSoilImageLayer,
  removeSoilImageLayer,
} from "../../../maps/utils/soilData";
import {
  abLineStripSignature,
  offsetsAreEquivalent,
  redistributeStripOffsets,
} from "./stripAbLineSync";
import { safeFitBounds } from "../../../maps/utils/bboxValidation";
import { useTrialsRouter } from "../../../shared/custom-hooks/useTrialsRouter";

const RGBA_SELECTED_FILL_BASIC_INFO = "rgba(49, 180, 242, 0.30)";
const RGBA_SELECTED_FILL_OTHER = "rgba(255, 255, 255, 0.10)";
const RGBA_UNSELECTED_FILL = "transparent";
const RGBA_SELECTED_LINE_BASIC_INFO = "#31B4F2";
const RGBA_SELECTED_LINE_OTHER = "white";
const RGBA_UNSELECTED_LINE = "white";
const TEXT_COLOR = "white";

// strips-flow : connect state and map for strips
// strips-flow : connect state and map for strips
const TrialMapComponent: React.FC<TrialMapComponentProps> = ({
  current,
  isMapFullscreen,
  isFertilizerTabActive,
}) => {
  const {
    createCustomMarkerElement,
    updateMapData,
    updateABWidth,
    checkPlotBoundaries,
    updateStripsData,
  } = useMapComponent();
  const {
    newTrialState,
    trialProtocolData,
    setNewTrialState,
    trialInfo,
    ratesDosage,
    plotsValidationData,
    setPlotsValidationData,
    activeRatesTab,
    autozoneRequestStatus,
    autozonesData,
    autozoneOpacity,
    fieldOperationsData,
    autozoneColorPalette,
    stripsData,
    setStripsData,
    soilOperationsData,
  } = useNewTrialState();
  const productivityZonesData = autozonesData?.productivity_zones;
  const isStripProtocol =
    trialProtocolData?.trial_application_method === "strip";
  const {
    fieldData,
    handleFieldSelection,
    isFieldDataLoading,
    fieldDataError,
  } = useFormHandling();

  const hasFieldData = useMemo(() => {
    if (!Array.isArray(fieldData) || fieldData.length === 0) return false;
    return fieldData.some((item: FieldDataInterface) => {
      const geom = item?.geometry;
      if (!geom) return false;
      if (geom.type === "Polygon") return !!geom.coordinates?.[0];
      if (geom.type === "MultiPolygon")
        return Array.isArray(geom.coordinates) && geom.coordinates.length > 0;
      return false;
    });
  }, [fieldData]);

  const showFieldDataOverlay = !isFieldDataLoading && !hasFieldData;
  const fieldDataOverlayMessageKey = fieldDataError
    ? "newTrial.trialMap.loadError"
    : "newTrial.trialMap.unavailable";
  const [isAutozonesLoaded, setIsAutozonesLoaded] = useState(false);
  const [isPlotGroupMisaligned, setIsPlotGroupMisaligned] = useState(false);
  const [isPlotGroupAligned, setIsPlotGroupAligned] = useState(false);
  const { router } = useTrialsRouter();
  const mapRef = useRef<IMapComponentRef>({
    getMap: () => null,
    mapContainerRef: null,
  });
  const mapObjectRef = useRef<mapboxgl.Map | null>(null); // Make mapRef globally accessible for SoilButtonComponent
  (window as any).mapRef = mapRef;

  // Keep the latest step in a ref so `onLoadMap` (which is captured once when the
  // map's "load" event binds) always reads the current step instead of the stale
  // value from first render. Without this, landing directly on plot/strip design
  // (edit mode, no step-by-step warm-up) creates `polygon-fill` with the initial
  // step 0 → the whole field stays highlighted/filled on those steps.
  const stepRef = useRef(current);
  stepRef.current = current;

  // Register / unregister the AB-line alignment callback (never for strip protocols)
  useEffect(() => {
    if (isStripProtocol) {
      registerAlignmentChangeCallback(null);
      return;
    }
    registerAlignmentChangeCallback((misaligned: boolean) => {
      setIsPlotGroupMisaligned(misaligned);
      if (misaligned) {
        setIsPlotGroupAligned(false);
      }
    });
    return () => {
      registerAlignmentChangeCallback(null);
    };
  }, [isStripProtocol]);

  // Hide banners whenever we leave the plot-design step
  useEffect(() => {
    if (current !== 1) {
      setIsPlotGroupMisaligned(false);
      setIsPlotGroupAligned(false);
    }
  }, [current]);

  // Update map polygon colours when switching steps
  useEffect(() => {
    const map = mapRef.current?.getMap();
    if (!map || !map.getLayer("polygon-fill")) return;

    const selectedFill =
      current === 0 ? RGBA_SELECTED_FILL_BASIC_INFO : RGBA_SELECTED_FILL_OTHER;
    const selectedLine =
      current === 0 ? RGBA_SELECTED_LINE_BASIC_INFO : RGBA_SELECTED_LINE_OTHER;

    map.setPaintProperty("polygon-fill", "fill-color", [
      "case",
      ["get", "selected"],
      selectedFill,
      RGBA_UNSELECTED_FILL,
    ]);
    // The field highlight fill is only meaningful while picking a field (step 0).
    // On plot design / strip design and later steps, hide the fill (the field
    // outline `polygon-line` still shows the boundary) so the field isn't
    // highlighted there.
    map.setPaintProperty("polygon-fill", "fill-opacity", current === 0 ? 1 : 0);
    map.setPaintProperty("polygon-line", "line-color", [
      "case",
      ["get", "selected"],
      selectedLine,
      RGBA_UNSELECTED_LINE,
    ]);
  }, [current]);

  const handleSnapToAbLine = (): void => {
    snapPlotsToAbLine(newTrialState, setNewTrialState);
    setIsPlotGroupMisaligned(false);
    setIsPlotGroupAligned(true);
  };

  const onLoadMap = (map: mapboxgl.Map): void => {
    map.addSource("geojson-field-data", {
      type: "geojson",
      data: {
        type: "FeatureCollection",
        features: [],
      },
    });

    // Add a separate source for field centroids (labels)
    map.addSource("geojson-field-labels", {
      type: "geojson",
      data: {
        type: "FeatureCollection",
        features: [],
      },
    });

    // Read the live step (via ref) rather than the value captured when this
    // handler was bound — the map "load" event can fire after the step has
    // already advanced (e.g. edit mode landing directly on plot/strip design).
    const step = stepRef.current;
    const selectedFill =
      step === 0 ? RGBA_SELECTED_FILL_BASIC_INFO : RGBA_SELECTED_FILL_OTHER;
    const selectedLine =
      step === 0 ? RGBA_SELECTED_LINE_BASIC_INFO : RGBA_SELECTED_LINE_OTHER;

    map.addLayer({
      id: "polygon-fill",
      type: "fill",
      source: "geojson-field-data",
      paint: {
        "fill-color": [
          "case",
          ["get", "selected"],
          selectedFill,
          RGBA_UNSELECTED_FILL,
        ],
        // Field highlight fill only on the field-selection step (step 0).
        "fill-opacity": step === 0 ? 1 : 0,
      },
    });

    map.addLayer({
      id: "polygon-line",
      type: "line",
      source: "geojson-field-data",
      paint: {
        "line-color": [
          "case",
          ["get", "selected"],
          selectedLine,
          RGBA_UNSELECTED_LINE,
        ],
        "line-width": 2,
      },
    });

    map.addLayer({
      id: "polygon-name",
      type: "symbol",
      source: "geojson-field-labels",
      layout: {
        "text-field": ["get", "name"],
        "text-size": 16,
        "text-anchor": "center",
        "text-allow-overlap": true,
        "text-ignore-placement": true,
        "symbol-z-order": "source",
      },
      paint: {
        "text-color": TEXT_COLOR,
      },
    });
  };

  useEffect(() => {
    const onForceClearStrips = () => {
      const map = mapObjectRef?.current;
      if (!map) return;

      clearStripLinesFromMap(mapObjectRef);
      clearStripLinesCache();
    };

    window.addEventListener("force-clear-strips", onForceClearStrips);
    return () => {
      window.removeEventListener("force-clear-strips", onForceClearStrips);
    };
  }, []);

  // 2) Hard guard: if strips should not exist, remove them
  useEffect(() => {
    const map = mapObjectRef?.current;
    if (!map) return;

    const noAbLine = (newTrialState?.ab_line?.length ?? 0) === 0;
    const stripModeOff =
      !newTrialState?.stripsData ||
      newTrialState.stripsData.isStripMode !== true ||
      !newTrialState.stripsData.appliedFromPopup;

    if (noAbLine || stripModeOff) {
      clearStripLinesFromMap(mapObjectRef);
      clearStripLinesCache();
    }
  }, [newTrialState?.ab_line, newTrialState?.stripsData]);

  // 3) Keep the applied strips spanning the CURRENT field. The stored offsets are
  // perpendicular distances from a specific AB line; when that line's direction/
  // position changes — or a persisted trial is reloaded — those distances no
  // longer line up with the field's grid, so many offset lines fall off the field
  // and the strips cover only part of it (the "half the field is empty" bug).
  // Rather than trying to detect *why* the offsets are stale (signature diffing
  // was unreliable — it can't tell already-stored-wrong offsets from correct
  // ones), we recompute the ideal offsets for the current AB line + field and, if
  // they differ from what's stored, rebuild the strip group exactly as clicking
  // Apply would. A configuration that already fits (fresh apply, or a manual
  // divider drag) yields equivalent offsets, so it is left untouched.
  const selectedStripAbLine = newTrialState?.ab_line?.find(
    (line: any) => line.selectedForStrips
  );
  const stripAbSignature = abLineStripSignature(
    selectedStripAbLine?.geojson?.geometry?.coordinates,
    selectedStripAbLine?.grid_width
  );
  // The field the strips must span. Its readiness is a dependency so the
  // recompute also runs once the field finishes loading (persisted-trial case),
  // not only when the AB line changes.
  const stripField = fieldData?.find(
    (item: any) => item.id === trialInfo?.field_ids?.[0]
  )?.geometry;
  const stripFieldReady = !!stripField;

  useEffect(() => {
    const currentStripsData = newTrialState?.stripsData;
    if (
      !selectedStripAbLine ||
      !stripField ||
      !currentStripsData?.isStripMode ||
      !currentStripsData?.appliedFromPopup ||
      (currentStripsData?.count ?? 0) < 2
    ) {
      return;
    }

    try {
      const requestedCount = currentStripsData.count;
      const { offsets: newOffsets } = redistributeStripOffsets(
        requestedCount,
        selectedStripAbLine?.geojson?.geometry?.coordinates,
        stripField,
        selectedStripAbLine?.grid_width ?? 50
      );
      // Couldn't compute fresh offsets (field/AB geometry unavailable) — leave
      // the existing strips as-is rather than wiping them.
      if (newOffsets.length === 0) return;
      // Already correct — a fresh apply or a still-valid manual divider drag
      // yields the same offsets. Don't churn (and don't discard the drag).
      if (offsetsAreEquivalent(newOffsets, currentStripsData.offsets)) return;

      // Strips snap to grid/grey lines, so a new AB direction may hold fewer than
      // requested — cap to what actually fits so count/offsets/cells stay in sync.
      const fittedCount = newOffsets.length + 1;
      const abLineWidth = selectedStripAbLine.grid_width ?? 50;

      // Keep the existing group's plot dimensions.
      const existingGroup = newTrialState.trial_plots?.find(
        (tp: any) => tp.plot?.isStripPlot
      );
      const plotWidth =
        existingGroup?.plot?.column_size ??
        existingGroup?.plot?.plotWidth ??
        50;
      const plotHeight =
        existingGroup?.plot?.row_size ?? existingGroup?.plot?.plotHeight ?? 50;

      // Rebuild the strip group from scratch — exactly like clicking Apply — so
      // the render tears down the old strip layers and redraws every strip fresh
      // from the new offsets (no stale/dropped strips).
      const stripGroup = {
        plot: {
          id: Date.now(),
          key: "Trial Plots",
          name: "Strips Group",
          visible: true,
          rows: 1,
          columns: fittedCount,
          column_size: plotWidth,
          row_size: plotHeight,
          plotWidth,
          plotHeight,
          isStripPlot: true,
          geometrySource: "manual",
          geojson: {
            type: "Feature",
            geometry: { type: "Point", coordinates: [0, 0] },
          },
          stripIndices: Array.from({ length: fittedCount }, (_, i) => i),
          totalStrips: fittedCount,
        },
        replicants: [],
      };

      clearStripLinesCache();
      clearStripLinesFromMap(mapObjectRef);
      setNewTrialState((prev: any) => ({
        ...prev,
        trial_plots: [stripGroup],
        stripsData: {
          ...prev.stripsData,
          isStripMode: true,
          appliedFromPopup: true,
          count: fittedCount,
          offsets: newOffsets,
          spacing:
            newOffsets.length > 1 ? newOffsets[1] - newOffsets[0] : abLineWidth,
          selectedAbLine: {
            name: selectedStripAbLine.name,
            gridWidth: abLineWidth,
            geojson: selectedStripAbLine.geojson,
          },
        },
      }));

      if (fittedCount < requestedCount) {
        toast.error(
          `Only ${fittedCount} strips fit for this AB-line direction.`
        );
      }
    } catch (error) {
      console.warn(
        "Failed to recompute strip offsets after AB-line change:",
        error
      );
    }
  }, [stripAbSignature, stripFieldReady]);

  useEffect(() => {
    const map = mapRef.current?.getMap();
    // if (map) {
    //   if (soilOperationsData) {
    //     addSoilImageLayer(map, soilOperationsData);
    //   } else {
    //     removeSoilImageLayer(map,soilOperationsData);
    //   }
    // }
    map?.on("load", () => {
      initializeMap(
        map,
        fieldData,
        trialInfo,
        newTrialState,
        current,
        handleFieldSelection
      );
      if (soilOperationsData) {
        addSoilImageLayer(
          map,
          soilOperationsData,
          soilOperationsData?.palette || "default",
          soilOperationsData?.opacity || 60
        );
      }
    });
  }, [trialInfo?.field_ids, fieldData, current, soilOperationsData]);

  useEffect(() => {
    if (fieldData) {
      const map = mapRef.current?.getMap();
      if (map) {
        const geoJsonData = initializeMap(
          map,
          fieldData,
          trialInfo,
          newTrialState,
          current,
          handleFieldSelection
        );
        if (
          current === 1 ||
          current === 2 ||
          current === 3 ||
          productivityZonesData?.results[0]?.predictions[0]?.productivity_zones
            .features.length > 0
        ) {
          updateMapData(
            map,
            mapObjectRef,
            current,
            !!isFertilizerTabActive,
            activeRatesTab,
            fieldData,
            newTrialState,
            setNewTrialState,
            createCustomMarkerElement,
            geoJsonData,
            plotsValidationData,
            setPlotsValidationData,
            autozoneRequestStatus,
            productivityZonesData,
            isAutozonesLoaded,
            setIsAutozonesLoaded,

            autozoneOpacity,
            autozoneColorPalette,

            fieldOperationsData,
            stripsData,
            setStripsData,
            updateStripsData,
            ratesDosage, // Pass ratesDosage from TrialMapComponent context
            soilOperationsData,
            soilOperationsData?.soilColorPalete ?? "",
            soilOperationsData?.soilZonesData ?? null
          );
          // Add soil image layer when updating map if soilOperationsData exists
          if (soilOperationsData) {
            addSoilImageLayer(
              map,
              soilOperationsData,
              soilOperationsData?.palette || "default",
              soilOperationsData?.opacity || 60
            );
          }
        }
      }
    }
  }, [
    fieldData,
    current,
    isFertilizerTabActive,
    activeRatesTab,
    newTrialState,
    setNewTrialState,
    createCustomMarkerElement,
    trialInfo,
    ratesDosage,
    autozoneRequestStatus,
    productivityZonesData,
    autozoneOpacity,
    autozoneColorPalette,
    soilOperationsData,
    fieldOperationsData,
    stripsData,
  ]);

  useEffect(() => {
    const abLines = newTrialState.ab_line;
    abLines?.forEach((abItem: any, i: number): void => {
      const map = mapRef.current?.getMap() as mapboxgl.Map;
      const sourceId = `grid-layer-${abItem.id ?? ""}`;
      const polygonGeoJSON = initializeMap(
        map,
        fieldData,
        trialInfo,
        newTrialState,
        current,
        handleFieldSelection
      );
      const source = map.getSource(sourceId);
      if (source) {
        // @ts-ignore
        const data = source._data;
        const bbox = turf.bbox(data);
        updateABWidth(
          abItem,
          sourceId,
          map,
          bbox,
          polygonGeoJSON,
          trialProtocolData
        );
      } else {
        console.error("source not found");
      }
    });
  }, [newTrialState]);

  /* below useEffect triggers plot bounds validation instantly when they are added */
  useEffect(() => {
    const map = mapRef.current?.getMap() as mapboxgl.Map;
    const polygonGeoJSON = initializeMap(
      map,
      fieldData,
      trialInfo,
      newTrialState,
      current,
      handleFieldSelection
    );

    if (polygonGeoJSON) {
      checkPlotBoundaries(
        polygonGeoJSON,
        plotsValidationData,
        setPlotsValidationData,
        newTrialState
      );
    }
  }, [newTrialState]);

  /**
   * Repaint plot and replicant cell borders whenever validation state changes.
   * Uses setPaintProperty to avoid the cost of a full updateMapData rebuild.
   * Turns borders RED when a plot (or replicant) is in an intersecting pair or
   * outside the field boundary; clears the border otherwise.
   */
  useEffect(() => {
    const map = mapRef.current?.getMap() as mapboxgl.Map;
    if (!map) return;

    newTrialState.trial_plots?.forEach((trialItem: any) => {
      const trialPlotId = trialItem?.plot?.id;
      const isStripPlot = trialItem?.plot?.isStripPlot;
      if (!trialPlotId) return;

      const totalCells =
        (trialItem?.plot?.rows ?? 0) * (trialItem?.plot?.columns ?? 0);

      const isError = isStripPlot
        ? false
        : plotsValidationData?.polygonsNotInsideField?.some(
            (id: any) => id === trialPlotId
          ) ||
          plotsValidationData?.intersectingPairs?.some((pair: any) =>
            pair.some((plotId: any) => plotId === trialPlotId)
          );

      for (let index = 0; index < totalCells; index++) {
        const cellBorderLayerId = `${trialPlotId}-${index}-border`;
        if (map.getLayer(cellBorderLayerId)) {
          map.setPaintProperty(
            cellBorderLayerId,
            "line-color",
            isError ? "rgba(255,0,0,1)" : "rgba(0,0,0,0)"
          );
        }
      }

      // Repaint replicant borders
      trialItem?.replicants?.forEach((replicant: any, cloneIndex: number) => {
        const replicantId = replicant?.id;
        const isReplicantError = isStripPlot
          ? false
          : plotsValidationData?.polygonsNotInsideField?.some(
              (id: any) => id === replicantId
            ) ||
            plotsValidationData?.intersectingPairs?.some((pair: any) =>
              pair.some((plotId: any) => plotId === replicantId)
            );

        for (let index = 0; index < totalCells; index++) {
          const cellBorderLayerId = `cloned-${cloneIndex}-${trialPlotId}-${index}-border`;
          if (map.getLayer(cellBorderLayerId)) {
            map.setPaintProperty(
              cellBorderLayerId,
              "line-color",
              isReplicantError ? "rgba(255,0,0,1)" : "rgba(0,0,0,0)"
            );
          }
        }
      });
    });
  }, [plotsValidationData, newTrialState.trial_plots]);

  useEffect(() => {
    const map = mapRef.current?.getMap();
    if (map) {
      map.resize();
    }
  }, [isMapFullscreen]);

  /* 
  below useEffect handles map focus at step1 and step2 in trial flow 
  by adjusting fit bounds of selected field 
  */

  useEffect(() => {
    if (!fieldData || fieldData.length === 0) {
      return;
    }

    const map = mapRef.current?.getMap();
    if (!map) {
      return;
    }

    // Build turf features straight from the real field geometries (Polygon or
    // MultiPolygon). Never flatten a MultiPolygon's parcels into one ring — that
    // produces an unclosed, self-intersecting polygon that makes turf.polygon throw
    // ("invalid selected field geometry") and yields the wrong bbox/zoom. turf.bbox
    // (inside safeFitBounds) handles Polygon, MultiPolygon and FeatureCollection.
    const toFieldFeature = (
      geom: FieldDataInterface["geometry"] | undefined
    ): turf.Feature<turf.Polygon | turf.MultiPolygon> | null =>
      geom && (geom.type === "Polygon" || geom.type === "MultiPolygon")
        ? (turf.feature(geom as any) as turf.Feature<
            turf.Polygon | turf.MultiPolygon
          >)
        : null;

    const fieldFeatures = fieldData
      .map((item: FieldDataInterface) => toFieldFeature(item?.geometry))
      .filter(Boolean) as Array<turf.Feature<turf.Polygon | turf.MultiPolygon>>;

    if (fieldFeatures.length === 0) {
      toast.error("invalid farm geometry:");
      router.goToTrialsList();
      return;
    }

    if (current === 0) {
      // Fit to every field on the farm.
      safeFitBounds(
        map,
        turf.featureCollection(fieldFeatures),
        "Invalid bounds for fields polygon"
      );
    } else if (current === 1) {
      // Fit to the selected field, including all parcels of a MultiPolygon.
      const selectedField = fieldData.find(
        (field: any) => field.id === trialInfo.field_ids[0]
      );
      const selectedFeature = toFieldFeature(selectedField?.geometry);
      if (!selectedFeature) {
        console.warn("No valid selected field geometry to fit map bounds.");
        return;
      }
      safeFitBounds(
        map,
        selectedFeature,
        "Invalid bounds for selected field polygon"
      );
    }
  }, [current, fieldData]);

  useEffect(() => {
    const map = mapRef.current?.getMap();
    // Remove soil layer whenever current is not 2 or on unmount
    if (map && current !== 2) {
      removeSoilImageLayer(map);
    }
    return () => {
      if (map) {
        removeSoilImageLayer(map);
      }
    };
  }, [current]);

  return (
    <div className="map" id="trial-map">
      {current !== 0 && <MapLayersDrawer />}

      <MapComponent onLoad={onLoadMap} ref={mapRef}>
        {showFieldDataOverlay && (
          <MapErrorOverlay messageKey={fieldDataOverlayMessageKey} />
        )}
        {current === 1 && !isStripProtocol && isPlotGroupAligned && (
          <AbLineAlignmentBanner
            variant="success"
            onClose={() => {
              setIsPlotGroupAligned(false);
            }}
          />
        )}
        {current === 1 &&
          !isStripProtocol &&
          isPlotGroupMisaligned &&
          !isPlotGroupAligned && (
            <AbLineAlignmentBanner
              variant="warning"
              onSnapToAbLine={handleSnapToAbLine}
              onClose={() => {
                setIsPlotGroupMisaligned(false);
              }}
            />
          )}
        {/* <div className="layer-paragraph">
          <div className="paragraph-content">
            <div className="paragraph-content-container">
              <p>{formatTranslation(t("newTrial.trialMap.layer"))}</p>
              <p>
                {formatTranslation(t("newTrial.trialMap.hide"))}
                <Button className="hide-btn">
                  <MinusOutlined />
                </Button>
              </p>
            </div>
            <div className="layers-main">
              <LayersOnMap />
              <div className="paragraph">
                <p>
                  <ExclamationCircleOutlined />{" "}
                  {formatTranslation(t("newTrial.trialMap.whatShows"))}
                </p>
              </div>
            </div>
          </div>
        </div> */}

        {trialInfo && trialInfo.trial_plots?.length > 0 && (
          <SubplotDetailsCard
            trailData={{
              ...trialInfo,
              trial_plots: newTrialState.trial_plots,
              stripsData: newTrialState.stripsData,
            }}
            trialProtocolData={trialProtocolData}
            ratesAndDosage={ratesDosage}
            properties={{}}
            current={current}
          />
        )}
      </MapComponent>
    </div>
  );
};

export default React.memo(TrialMapComponent);
